import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, SelectChangeEvent, TextField, TextFieldProps, Theme, Tooltip, Typography } from '@mui/material';
import {
  ETranscoderProfileType,
  TranscoderAudioProfile,
  TranscoderProfile,
  TranscoderVideoProfile,
} from '@vxui/common';
import { SelectBox } from '@vxui/components';
import { FormikErrors } from 'formik';
import { TFunction } from 'i18next';
import { ChangeEvent, FC, ReactElement, RefObject } from 'react';
import { TableColumn } from 'react-data-table-component';
import { codecPassthroughValue } from '../../../models';
import { LanguageField } from '../forms/LanguageField';
import { cellSelectBoxStyle } from '../forms/profileForm.styles';
import { CellProps, CellSelectBoxProps, EInputType } from './profilesTable.types';
import { filterByTemplate } from './templateFilter.helpers';

export const ColumnName = (t: TFunction, column: string, unit?: string): ReactElement => {
  const name: string = t(`transcoder.columns.${column}`);

  return (
    <Typography component="span">
      <Typography variant="p14" component="span" sx={{ textTransform: 'capitalize' }}>
        {name}
      </Typography>
      <Typography variant="p14" component="span" sx={{ textTransform: 'none' }}>
        {unit && ` (${t(`transcoder.unit.${unit}`)})`}
      </Typography>
    </Typography>
  );
};

type CellInputProps = TextFieldProps & {
  row?: TranscoderProfile;
  onChangeCellHandler?: (row: TranscoderProfile, fieldName?: string) => void;
};

export const CellInput: FC<CellInputProps> = (componentProps: CellInputProps) => {
  const { row, onChangeCellHandler, ...props } = componentProps;
  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.name && row && onChangeCellHandler) {
      let text: string | number = event.target.value;
      if (props.type && props.type === EInputType.number && text) {
        text = +text;
      }

      onChangeCellHandler({ ...row, [props.name]: text }, props.name);
    }
  };

  const { id = props.id ?? props.name, onChange, ...textFieldProps } = props;
  return <TextField id={id} {...textFieldProps} onChange={onChange ?? onChangeHandler} size="small" />;
};

export const CellSelectBox = <T extends string | number>({
  fieldName,
  onChangeHandler,
  row,
  value,
  items,
  placeholder,
  disabled,
}: CellSelectBoxProps<T>): ReactElement => {
  const error =
    row.errorMsg &&
    row.errorMsg[fieldName as keyof (FormikErrors<TranscoderAudioProfile> | FormikErrors<TranscoderVideoProfile>)];

  return (
    <SelectBox
      value={value as string}
      onChange={(event: SelectChangeEvent) => onChangeHandler(event, row)}
      sx={cellSelectBoxStyle}
      placeholder={placeholder}
      disabled={disabled}
      size="small"
      error={!!error}
      helperText={error}
      showNotMapped
    >
      {items}
    </SelectBox>
  );
};

export const CellLanguageInput = ({
  fieldName,
  onChangeHandler,
  row,
  value,
  placeholder,
  disabled,
}: CellProps<string>): ReactElement => {
  const error =
    row.errorMsg &&
    row.errorMsg[fieldName as keyof (FormikErrors<TranscoderAudioProfile> | FormikErrors<TranscoderVideoProfile>)];

  return (
    <LanguageField
      value={value ?? ''}
      onChange={(_, value) => onChangeHandler({ target: { value } } as SelectChangeEvent, row)}
      sx={{ ...cellSelectBoxStyle }}
      size="small"
      disabled={disabled}
      error={!!error}
      helperText={error}
      placeholder={placeholder}
    />
  );
};

const filterByName = (profile: TranscoderProfile, filterText: string): boolean =>
  profile.name.toLowerCase().indexOf(filterText) > -1;

export const filter = (filterText: string, templateId: string) => (profile: TranscoderProfile) => {
  return (
    profile.type === ETranscoderProfileType.new ||
    ((filterText ? filterByName(profile, filterText) : true) &&
      (templateId === '' ? true : filterByTemplate(profile, templateId)))
  );
};

export const scrollProfilesTable = (containerRef: RefObject<HTMLDivElement>) => {
  if (containerRef.current) {
    const tableBody = containerRef.current.querySelector('.profiles-table');
    if (tableBody) {
      tableBody.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }
};

export const editRow = <T extends TranscoderAudioProfile | TranscoderVideoProfile>(
  row: T,
  editableRows?: T[],
): T | undefined => editableRows?.find(editRow => editRow.id === row.id);

export const getActions = <T extends TranscoderAudioProfile | TranscoderVideoProfile>(
  t: TFunction,
  onEditHandler: (profile: T) => void,
  onDeleteHandler: (profile: T) => void,
  onSaveHandler: (profile: T) => void,
  onCancelHandler: (profile: T) => void,
  editableRows?: T[],
): TableColumn<T>[] => [
  {
    cell: row => {
      const editableRow = editRow(row, editableRows);
      const disabled = editableRow?.updating ? true : false;
      const fillColorStyle = (theme: Theme) => ({
        path: {
          fill: disabled ? theme.palette.primary[50] : theme.palette.common.white,
        },
      });
      const buttons = !editableRow
        ? [
            <Tooltip key={`edit-${row.id}`} title={t('transcoder.profile.action.edit')}>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                onClickCapture={() => onEditHandler(row)}
                sx={{ padding: '4px', minWidth: '32px' }}
              >
                <EditIcon fontSize="small" />
              </Button>
            </Tooltip>,
          ]
        : [
            <Tooltip key={`save-${row.id}`} title={t('transcoder.profile.action.save')}>
              <Button
                variant="contained"
                size="small"
                onClickCapture={() => onSaveHandler(row)}
                sx={{ padding: '4px', minWidth: '32px' }}
                disabled={disabled}
                color="success"
              >
                <CheckIcon fontSize="small" color="primary" sx={fillColorStyle} />
              </Button>
            </Tooltip>,
            <Tooltip key={`cancel-${row.id}`} title={t('transcoder.profile.action.cancel')}>
              <Button
                variant="contained"
                size="small"
                color="tertiary"
                onClickCapture={() => onCancelHandler(row)}
                sx={{ padding: '4px', minWidth: '32px', marginLeft: '8px' }}
                disabled={disabled}
              >
                <CloseIcon fontSize="small" />
              </Button>
            </Tooltip>,
          ];

      if (row.type !== ETranscoderProfileType.new) {
        buttons.push(
          <Tooltip key={`delete-${row.id}`} title={t('transcoder.profile.action.delete')}>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => onDeleteHandler(row)}
              sx={{ padding: '4px', minWidth: '32px', marginLeft: '25px' }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Tooltip>,
        );
      }

      return buttons;
    },
    sortable: false,
    width: '120px',
  },
];

export const isPassthroughAudio = (profile: TranscoderAudioProfile) => codecPassthroughValue === profile?.codec;
